/**
 * client/input/stick.ts — raw gamepad conditioning (pure, DOM-free, unit-tested).
 *
 * OWNER: INPUT agent.
 * Sources:
 *  - research/controller.md §3.1 radial stick deadzone (T5 win_gamepad.cpp GPad_ConvertStickToFloat),
 *    gpad_stick_deadzone_min 0.2 / _max 0.01 (T6 dvar sites 0x4e4d21 / 0x4e4d64).
 *  - §3.2 virtual axes LINEAR / SQUARED (T5 cl_gamepad.cpp CL_GamepadAxisValue).
 *  - §2.2 stick layouts (tools/src/bo2-raw/thumbstick_*.cfg via research/data/controller.json).
 *  - §3.3 movement scale (T5 cl_input_mp.cpp CL_GamepadMove).
 *  - §3.4 trigger deadzone gpad_button_deadzone 0.13 (T6 dvar site 0x4e4cde; T5 GPad_UpdateAnalogs).
 */
import type { StickLayout } from '../settings/settings';
import type { Knots } from './controller-data.generated';

export const GPAD_STICK_DEADZONE_MIN = 0.2; // [CTL §3.1] DVAR 0x4e4d21
export const GPAD_STICK_DEADZONE_MAX = 0.01; // [CTL §3.1] DVAR 0x4e4d64 (outer)
export const GPAD_BUTTON_DEADZONE = 0.13; // [CTL §3.4] DVAR 0x4e4cde

/**
 * GPad_ConvertStickToFloat: radial inner deadzone 0.2, outer 0.01, magnitude rescaled, direction kept.
 * x/y in [-1,1] (any sign convention; it is preserved). Writes into out.
 */
export function conditionStick(out: [number, number], x: number, y: number): [number, number] {
  const len = Math.sqrt(x * x + y * y);
  if (len < GPAD_STICK_DEADZONE_MIN || len === 0) { out[0] = 0; out[1] = 0; return out; }
  let n: number;
  if (len <= 1 - GPAD_STICK_DEADZONE_MAX) n = (len - GPAD_STICK_DEADZONE_MIN) / (1 - (GPAD_STICK_DEADZONE_MIN + GPAD_STICK_DEADZONE_MAX));
  else n = 1;
  out[0] = (x / len) * n;
  out[1] = (y / len) * n;
  return out;
}

/** GPad_UpdateAnalogs: analog = max(0, (raw - 0.13) / 0.87). Pressed when > 0 [CTL §3.4]. */
export function triggerAnalog(raw: number): number {
  const v = (raw - GPAD_BUTTON_DEADZONE) / (1 - GPAD_BUTTON_DEADZONE);
  return v > 0 ? Math.min(1, v) : 0;
}

export type VirtualAxisName = 'side' | 'forward' | 'yaw' | 'pitch';
export type MapType = 'linear' | 'squared';
export type PhysAxis = 'LX' | 'LY' | 'RX' | 'RY';
export type StickLayoutDef = Readonly<Record<PhysAxis, readonly [VirtualAxisName, MapType]>>;

/** thumbstick_*.cfg [CTL §2.2]. */
export const STICK_LAYOUTS: Readonly<Record<StickLayout, StickLayoutDef>> = {
  default: { LX: ['side', 'squared'], LY: ['forward', 'squared'], RX: ['yaw', 'linear'], RY: ['pitch', 'linear'] },
  southpaw: { RX: ['side', 'squared'], RY: ['forward', 'squared'], LX: ['yaw', 'linear'], LY: ['pitch', 'linear'] },
  legacy: { LX: ['yaw', 'linear'], LY: ['forward', 'squared'], RX: ['side', 'squared'], RY: ['pitch', 'linear'] },
  legacysouthpaw: { RX: ['yaw', 'linear'], RY: ['forward', 'squared'], LX: ['side', 'squared'], LY: ['pitch', 'linear'] },
};

export interface VirtualAxes {
  /** Strafe, right = +. */
  side: number;
  /** Forward, stick up = +. */
  forward: number;
  /** Stick right = + (CL_GamepadMove negates it into Quake yaw). */
  yaw: number;
  /** Stick up = + (CL_GamepadMove multiplies by invert ? 1 : -1). */
  pitch: number;
}

export function createVirtualAxes(): VirtualAxes {
  return { side: 0, forward: 0, yaw: 0, pitch: 0 };
}

/**
 * CL_GamepadAxisValue for all four virtual axes. L/R are CONDITIONED sticks with +y = UP
 * (flip the browser's +y-down before calling). SQUARED: v = a * |stick| (radial, keeps direction).
 */
export function mapVirtualAxes(out: VirtualAxes, L: readonly [number, number], R: readonly [number, number], layout: StickLayout): VirtualAxes {
  const def = STICK_LAYOUTS[layout] ?? STICK_LAYOUTS.default;
  out.side = 0; out.forward = 0; out.yaw = 0; out.pitch = 0;
  const lLen = Math.sqrt(L[0] * L[0] + L[1] * L[1]);
  const rLen = Math.sqrt(R[0] * R[0] + R[1] * R[1]);
  assign(out, def.LX, L[0], lLen);
  assign(out, def.LY, L[1], lLen);
  assign(out, def.RX, R[0], rLen);
  assign(out, def.RY, R[1], rLen);
  return out;
}
function assign(out: VirtualAxes, m: readonly [VirtualAxisName, MapType], a: number, stickLen: number): void {
  out[m[0]] = m[1] === 'squared' ? a * stickLen : a;
}

/** ClampChar. */
export function clampChar(i: number): number {
  return i < -128 ? -128 : i > 127 ? 127 : i;
}

/**
 * CL_GamepadMove movement part [CTL §3.3]: square-to-circle scale so a full diagonal is (127,127),
 * truncation toward zero (C float->int). Returns [forwardmove, rightmove] (not yet summed/clamped).
 */
export function gamepadMove(out: [number, number], forward: number, side: number): [number, number] {
  let moveScale = 127;
  const af = Math.abs(forward), as = Math.abs(side);
  if (as > 0 || af > 0) {
    const r = as <= af ? side / forward : forward / side;
    moveScale *= Math.sqrt(r * r + 1);
  }
  out[0] = Math.trunc(Math.fround(forward * moveScale));
  out[1] = Math.trunc(Math.fround(side * moveScale));
  return out;
}

/** GraphGetValueFromFraction: piecewise linear over knots (x ascending, last x = 1). */
export function graphValue(knots: Knots, fraction: number): number {
  const f = fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;
  for (let i = 1; i < knots.length; i++) {
    const k1 = knots[i];
    if (k1[0] >= f) {
      const k0 = knots[i - 1];
      const span = k1[0] - k0[0];
      const t = span > 0 ? (f - k0[0]) / span : 0;
      return k0[1] + (k1[1] - k0[1]) * t;
    }
  }
  return knots[knots.length - 1][1];
}
